import React, { useEffect, useState } from 'react'
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Collapse,
  TableSortLabel,
  Stack,
  TextField,
  IconButton
} from '@mui/material'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import TableLoading from 'components/TableLoading'
import {
  getWoodlandOrders,
  SortField,
  SortDirection,
  WoodlandOrder
} from 'api/orders'
import { getOrderStatusColor, OrderStatus } from 'utils/orderConstants'

const OrdersPage = () => {
  const [orders, setOrders] = useState<WoodlandOrder[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [sortField, setSortField] = useState<SortField>(SortField.CREATED_AT)
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.DESC
  )
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const fetchOrders = async (
    page: number,
    pageLimit: number,
    sortField: SortField,
    sortDirection: SortDirection,
    salesOrderId?: string,
    status?: string
  ) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await getWoodlandOrders({
        page,
        limit: pageLimit,
        sortBy: sortField,
        sortDir: sortDirection,
        salesOrderId: salesOrderId || undefined,
        status: status || undefined
      })
      setOrders(response.orders)
      setTotalPages(response.totalPages)
      setTotalRecords(response.totalOrders)
      setCurrentPage(response.currentPage)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch orders')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders(currentPage, limit, sortField, sortDirection)
  }, [])

  const handlePageChange = (_: any, newPage: number) => {
    fetchOrders(
      newPage,
      limit,
      sortField,
      sortDirection,
      searchInput,
      statusFilter
    )
  }

  const handleLimitChange = (e: SelectChangeEvent<string>) => {
    const newLimit = parseInt(e.target.value, 10)
    setLimit(newLimit)
    fetchOrders(
      1,
      newLimit,
      sortField,
      sortDirection,
      searchInput,
      statusFilter
    )
  }

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === SortDirection.ASC
    const newDirection = isAsc ? SortDirection.DESC : SortDirection.ASC
    setSortField(field)
    setSortDirection(newDirection)
    fetchOrders(1, limit, field, newDirection, searchInput, statusFilter)
  }

  const handleSearch = () => {
    fetchOrders(1, limit, sortField, sortDirection, searchInput, statusFilter)
  }

  const handleClearSearch = () => {
    setSearchInput('')
    fetchOrders(1, limit, sortField, sortDirection, '', statusFilter)
  }

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    const newStatus = e.target.value
    setStatusFilter(newStatus)
    fetchOrders(1, limit, sortField, sortDirection, searchInput, newStatus)
  }

  const toggleRow = (index: number) => {
    const newExpanded = new Set(expandedRows)
    newExpanded.has(index) ? newExpanded.delete(index) : newExpanded.add(index)
    setExpandedRows(newExpanded)
  }

  return (
    <Container sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 3 }}>
          <ShoppingCartIcon fontSize='large' color='primary' />
          <Typography variant='h4'>Orders</Typography>
        </Stack>

        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label='Search by Sales Order ID'
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSearch()
              }
            }}
            InputProps={{
              endAdornment: (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {searchInput && (
                    <IconButton
                      size='small'
                      onClick={handleClearSearch}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  )}
                  <IconButton
                    size='small'
                    onClick={handleSearch}
                    color='primary'
                  >
                    <SearchIcon fontSize='small' />
                  </IconButton>
                </Box>
              )
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id='status-filter-label'>Filter by Status</InputLabel>
            <Select
              labelId='status-filter-label'
              value={statusFilter}
              label='Filter by Status'
              onChange={handleStatusChange}
            >
              <MenuItem value=''>All Statuses</MenuItem>
              <MenuItem value={OrderStatus.AWAITING_PAYMENT}>
                Awaiting Payment
              </MenuItem>
              <MenuItem value={OrderStatus.PAID}>Paid</MenuItem>
              <MenuItem value={OrderStatus.PAYMENT_FAILED}>
                Payment Failed
              </MenuItem>
              <MenuItem value={OrderStatus.NETSUITE_FAILED}>
                NetSuite Failed
              </MenuItem>
              <MenuItem value={OrderStatus.OFFLINE_PAYMENT}>
                Offline Payment
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant='body1' color='text.secondary'>
            Total Records: {totalRecords}
          </Typography>
          <FormControl size='small' sx={{ minWidth: 120 }}>
            <InputLabel id='limit-label'>Per Page</InputLabel>
            <Select
              labelId='limit-label'
              value={String(limit)}
              label='Per Page'
              onChange={handleLimitChange}
            >
              <MenuItem value='10'>10</MenuItem>
              <MenuItem value='25'>25</MenuItem>
              <MenuItem value='50'>50</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Typography color='error' sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === SortField.SALES_ORDER_ID}
                    direction={
                      sortField === SortField.SALES_ORDER_ID
                        ? (sortDirection.toLowerCase() as 'asc' | 'desc')
                        : 'asc'
                    }
                    onClick={e => {
                      e.stopPropagation()
                      handleSort(SortField.SALES_ORDER_ID)
                    }}
                  >
                    Order ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>Ref ID</TableCell>
                <TableCell>Company Name</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='right'>Subtotal</TableCell>
                <TableCell align='right'>Discount</TableCell>
                <TableCell align='right'>Tax</TableCell>
                <TableCell align='right'>Assembly Fee</TableCell>
                <TableCell align='right'>Shipping Fee</TableCell>
                <TableCell align='right'>Convenience Fee</TableCell>
                <TableCell align='right'>Final Amount</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === SortField.CREATED_AT}
                    direction={
                      sortField === SortField.CREATED_AT
                        ? (sortDirection.toLowerCase() as 'asc' | 'desc')
                        : 'asc'
                    }
                    onClick={e => {
                      e.stopPropagation()
                      handleSort(SortField.CREATED_AT)
                    }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={13}>
                    <TableLoading />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align='center'>
                    No orders found
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order, index) => {
                  const subtotal = order.orderItems?.reduce(
                    (sum: number, item: any) =>
                      sum + item.unitPrice * item.quantity,
                    0
                  )

                  return (
                    <React.Fragment key={order.id}>
                      <TableRow
                        hover
                        onClick={() => toggleRow(index)}
                        sx={{
                          '& > *': { borderBottom: 'unset' },
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <TableCell>{order.salesOrderId}</TableCell>
                        <TableCell>{order.refId}</TableCell>
                        <TableCell>{order.companyName || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {order.paymentMethod
                              ? (
                                  {
                                    CREDIT_CARD: 'Credit Card',
                                    CHECK: 'Check / Money Order',
                                    ACH_WIRE_TRANSFER: 'ACH / Wire Transfer',
                                    PAY_AT_PICKUP: 'Pay at Pickup'
                                  } as Record<string, string>
                                )[order.paymentMethod] ?? order.paymentMethod
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.status
                              .split('_')
                              .map(
                                (word: string) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase()
                              )
                              .join(' ')}
                            size='small'
                            color={getOrderStatusColor(order.status)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          ${subtotal?.toFixed(2) ?? '0.00'}
                        </TableCell>
                        <TableCell align='right'>
                          ${order.discountAmount.toFixed(2)}
                        </TableCell>
                        <TableCell align='right'>
                          ${order.taxAmount.toFixed(2)}
                        </TableCell>
                        <TableCell align='right'>
                          ${order.assemblyFee.toFixed(2)}
                        </TableCell>
                        <TableCell align='right'>
                          ${order.shippingFee.toFixed(2)}
                        </TableCell>
                        <TableCell align='right'>
                          ${order.convenienceFee.toFixed(2)}
                        </TableCell>
                        <TableCell align='right'>
                          ${order.finalAmount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {new Date(order.createdAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={13}
                        >
                          <Collapse
                            in={expandedRows.has(index)}
                            timeout='auto'
                            unmountOnExit
                          >
                            <Box sx={{ margin: 2 }}>
                              {order.pickupTime && (
                                <Box mb={2}>
                                  <Typography variant='subtitle1' gutterBottom>
                                    Pickup Time
                                  </Typography>
                                  <Typography variant='body2'>
                                    {(() => {
                                      const parts = order.pickupTime.split(' ')
                                      if (parts.length === 2) {
                                        const date = new Date(parts[0])
                                        const timeSlot =
                                          parts[1].charAt(0).toUpperCase() +
                                          parts[1].slice(1)
                                        return `${date.toLocaleDateString(
                                          'en-US',
                                          {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                          }
                                        )} - ${timeSlot}`
                                      }
                                      return order.pickupTime
                                    })()}
                                  </Typography>
                                </Box>
                              )}

                              {order.comment && (
                                <Box mb={2}>
                                  <Typography variant='subtitle1' gutterBottom>
                                    Comment
                                  </Typography>
                                  <Typography variant='body2'>
                                    {order.comment}
                                  </Typography>
                                </Box>
                              )}

                              <Typography variant='subtitle1' gutterBottom>
                                Order Items
                              </Typography>
                              <Table size='small'>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>SKU</TableCell>
                                    <TableCell align='right'>Qty</TableCell>
                                    <TableCell align='right'>
                                      Unit Price
                                    </TableCell>
                                    <TableCell align='right'>Total</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {order.orderItems.map(
                                    (item: any, i: number) => (
                                      <TableRow key={i}>
                                        <TableCell>{item.itemName}</TableCell>
                                        <TableCell align='right'>
                                          {item.quantity}
                                        </TableCell>
                                        <TableCell align='right'>
                                          ${item.unitPrice.toFixed(2)}
                                        </TableCell>
                                        <TableCell align='right'>
                                          $
                                          {(
                                            item.unitPrice * item.quantity
                                          ).toFixed(2)}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>

                              {order.transactionDetails && (
                                <Box mt={3}>
                                  <Typography variant='subtitle1' gutterBottom>
                                    Payment Info
                                  </Typography>
                                  <Typography variant='body2'>
                                    {order.transactionDetails.cardType} ****
                                    {order.transactionDetails.cardNumber}
                                  </Typography>
                                  <Typography variant='body2'>
                                    Amount: $
                                    {order.transactionDetails.amount.toFixed(2)}
                                  </Typography>
                                  <Typography variant='body2'>
                                    Transaction ID:{' '}
                                    {order.transactionDetails.transactionId}
                                  </Typography>
                                  {order.transactionDetails.date && (
                                    <Typography
                                      variant='body2'
                                      color='text.secondary'
                                    >
                                      Date:{' '}
                                      {new Date(
                                        order.transactionDetails.date
                                      ).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </Typography>
                                  )}

                                  {order.transactionDetails.billingAddress && (
                                    <Box mt={1} ml={1}>
                                      <Typography
                                        variant='subtitle2'
                                        gutterBottom
                                      >
                                        Billing Address
                                      </Typography>
                                      <Typography variant='body2'>
                                        {
                                          order.transactionDetails
                                            .billingAddress.firstName
                                        }{' '}
                                        {
                                          order.transactionDetails
                                            .billingAddress.lastName
                                        }
                                      </Typography>
                                      <Typography variant='body2'>
                                        {
                                          order.transactionDetails
                                            .billingAddress.address
                                        }
                                      </Typography>
                                      <Typography variant='body2'>
                                        {
                                          order.transactionDetails
                                            .billingAddress.city
                                        }
                                        ,{' '}
                                        {
                                          order.transactionDetails
                                            .billingAddress.state
                                        }{' '}
                                        {
                                          order.transactionDetails
                                            .billingAddress.zip
                                        }
                                      </Typography>
                                      <Typography variant='body2'>
                                        {
                                          order.transactionDetails
                                            .billingAddress.country
                                        }
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box display='flex' justifyContent='center' mt={4}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color='primary'
            />
          </Box>
        )}
      </Paper>
    </Container>
  )
}

export default OrdersPage
